import { useGetPayoutHistoryApiV1Suspense } from "@/api/__generated__/api.schema";
import { type PayoutHistoryItem, PayoutHistoryItemStatus } from "@/api/__generated__/models";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { usePayoutMethods } from "@/routes/payout/hooks/use-payout-methods";
import { usePayoutTaxInfo } from "@/routes/payout/hooks/use-tax-info";
import { refineTimezone } from "@/utils/date";
import { formatCurrency } from "@/utils/format";
import {
  Alert,
  Badge,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from "@backpac/monica";
import { Temporal } from "temporal-polyfill";
import { match } from "ts-pattern";

export function PayoutInvoiceContents() {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items },
    },
  } = useGetPayoutHistoryApiV1Suspense(workspaceId);
  const { taxInfo } = usePayoutTaxInfo();
  const {
    data: { payoutMethods },
  } = usePayoutMethods();
  const hasPayoutMethods = payoutMethods.length > 0;

  return (
    <section className="space-y-4">
      {hasPayoutMethods && taxInfo == null ? (
        <Alert theme="red" className="mb-6">
          To receive payouts, please submit your tax form as required for tax reporting purposes.
        </Alert>
      ) : null}

      <Table
        border={false}
        head={
          <div className="px-6 mb-7">
            <Typography size="lg" color="black" font="unbounded">
              Withdrawal History
            </Typography>
          </div>
        }
      >
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-4 desktop:pl-6">Date</TableHead>
            <TableHead className="pl-4 desktop:pl-6">Status</TableHead>
            <TableHead className="pr-4 desktop:pr-6 text-right">Amount</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <PayoutInvoiceContents.Empty />
          ) : (
            items.map((item: PayoutHistoryItem) => (
              <TableRow key={item.startAt}>
                <TableCell className="pl-4 desktop:pl-6 py-6">
                  {refineTimezone(
                    Temporal.PlainDateTime.from(item.startAt).toLocaleString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }),
                  )}
                </TableCell>
                <TableCell className="py-6">
                  <PayoutInvoiceContents.Status status={item.status} />
                </TableCell>
                <TableCell className="text-right font-semibold pr-4 desktop:pr-6 py-6">
                  {formatCurrency(item.amount)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </section>
  );
}

PayoutInvoiceContents.Empty = function ChannelTableEmpty() {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={5} className="text-center">
        <div className="space-y-3 py-12 text-white">
          <Icon name="invoice" bgColor="brandSkyblue" iconSize="24px" className="size-14" />
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No withdrawal history
            </Typography>
            <Typography size="xs" color="textMuted">
              Withdrawal history will appear here
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

PayoutInvoiceContents.Status = function PayoutInvoiceContentsStatus({ status }: { status: PayoutHistoryItemStatus }) {
  return match(status)
    .with(PayoutHistoryItemStatus.COMPLETED, () => (
      <Tooltip>
        <TooltipTrigger>
          <Badge theme="green">Sent</Badge>
        </TooltipTrigger>
        <TooltipContent>Payment sent. Arrival time depends on your payout method.</TooltipContent>
      </Tooltip>
    ))
    .with(PayoutHistoryItemStatus.PENDING, () => (
      <Tooltip>
        <TooltipTrigger>
          <Badge theme="orange">In progress</Badge>
        </TooltipTrigger>
        <TooltipContent>Processing your withdrawal. It may take a few minutes to hours.</TooltipContent>
      </Tooltip>
    ))
    .with(PayoutHistoryItemStatus.FAILED, () => (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-2">
            <Badge theme="red">Failed</Badge>
            <i className="i-bp-exclamation inline-block size-3" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          Payment failed. This usually happens when your payout information is incomplete or incorrect. Please review
          and update your details.
        </TooltipContent>
      </Tooltip>
    ))
    .with(PayoutHistoryItemStatus.RETURNED, () => (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-2">
            <Badge theme="red">Returned</Badge>
            <i className="i-bp-exclamation inline-block size-3" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          Payment returned. This may happen if the account is closed or invalid. Please verify your payout information
          or try a different payout method.
        </TooltipContent>
      </Tooltip>
    ))
    .exhaustive();
};
